import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "../services/api";
import { 
  ChevronLeft, 
  AlertCircle, 
  Settings, 
  ArrowRight,
  ShieldCheck,
  Loader2
} from "lucide-react";

interface Question {
  id: number;
  title: string;
  weight: number;
  image: string | null;
}

const Problems: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { deviceId, variantId } = useParams();

  const { groupId, globalAnswers, defectAnswers } = location.state || {};

  const [questions, setQuestions] = useState<Question[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  // --- CORE LOGIC (UNTOUCHED) ---
  useEffect(() => {
    if (!groupId) {
      setLoading(false);
      return;
    }

    api.get(`/inspection/group/${groupId}/functional`)
      .then((data) => setQuestions(data || []))
      .catch((err) => console.error("Problems Fetch Error:", err))
      .finally(() => setLoading(false));
  }, [groupId]);

  const toggle = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-10 h-10 text-[#363C90] animate-spin" />
        <p className="text-slate-400 text-sm font-bold uppercase tracking-widest italic">Running Internal <br/> Component Audit...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-32">
      
      {/* --- SLIM HEADER --- */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-30">
        <div className="max-w-[1200px] mx-auto px-4 py-4 md:py-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-[#363C90] transition-colors mb-3 text-[10px] font-black uppercase tracking-widest"
          >
            <ChevronLeft size={14} /> Previous Step
          </button>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight leading-none">Internal Issues</h1>
              <p className="text-slate-400 text-[13px] font-medium mt-1.5 italic">Select hardware features that are NOT working.</p>
            </div>

            <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all duration-300 ${
              selected.length === 0 
              ? "bg-emerald-50 border-emerald-100 text-emerald-600" 
              : "bg-rose-50 border-rose-100 text-rose-600"
            }`}>
              {selected.length === 0 ? <ShieldCheck size={16} /> : <AlertCircle size={16} />}
              <p className="text-[10px] font-black uppercase tracking-wider">
                {selected.length === 0 ? "Hardware Status: Healthy" : `${selected.length} Internal Faults Found`}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* --- PROBLEMS GRID (CRITICAL UI) --- */}
      <div className="max-w-[1200px] mx-auto px-4 mt-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {questions.map((item) => {
            const isActive = selected.includes(item.id);
            return (
              <div
                key={item.id}
                onClick={() => toggle(item.id)}
                className={`
                  group relative cursor-pointer bg-white rounded-2xl p-4 border transition-all duration-200 flex flex-col items-center text-center
                  ${isActive 
                    ? "border-rose-500 bg-rose-50/30 shadow-md shadow-rose-100/50 ring-1 ring-rose-500" 
                    : "border-slate-100 hover:border-slate-200 hover:shadow-lg"}
                `}
              >
                {/* Warning Badge Indicator */}
                <div className={`
                  absolute top-3 right-3 w-5 h-5 rounded-lg border flex items-center justify-center transition-all
                  ${isActive ? "bg-rose-500 border-rose-500" : "bg-white border-slate-200"}
                `}>
                  {isActive ? (
                    <AlertCircle size={12} className="text-white" strokeWidth={3} />
                  ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-200" />
                  )}
                </div>

                <div className={`w-14 h-14 md:w-16 md:h-16 mb-3 rounded-xl flex items-center justify-center transition-colors border ${
                   isActive ? "bg-white border-rose-100" : "bg-slate-50 border-slate-50"
                }`}>
                  {item.image ? (
                    <img src={item.image} alt={item.title} className="w-full h-full object-contain p-2 group-hover:scale-110 transition-transform duration-300" />
                  ) : (
                    <Settings className={`${isActive ? "text-rose-300" : "text-slate-200"}`} size={24} />
                  )}
                </div>

                <h3 className={`text-[12px] md:text-[13px] font-bold leading-tight px-1 ${isActive ? "text-rose-700" : "text-slate-600"}`}>
                  {item.title}
                </h3>
              </div>
            );
          })}
        </div>

        <div className="mt-8 p-6 rounded-2xl border-2 border-dashed border-slate-200 bg-white/50 text-center">
            <p className="text-slate-400 text-sm font-bold italic">No internal problems? You're good to go.</p>
        </div>
      </div>

      {/* --- SHARP FLOATING FOOTER --- */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-100 p-4 z-40">
        <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row items-center justify-between gap-4 px-2">
          <div className="text-center md:text-left">
             <span className="text-[9px] font-black text-slate-400 uppercase tracking-[2px]">Step 3 of 4</span>
             <p className="text-lg font-black text-slate-900 leading-none mt-1">Audit Complete</p>
          </div>

          <button
            onClick={() => navigate(`/sell/age/${deviceId}/${variantId}`, { 
              state: { 
                deviceId, 
                variantId, 
                groupId, 
                globalAnswers, 
                defectAnswers,
                functionalAnswers: selected 
              } 
            })}
            className="w-full md:w-auto px-10 py-4 bg-[#363C90] hover:bg-[#2a2f70] text-white rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all hover:translate-x-1 shadow-lg shadow-indigo-900/10 uppercase tracking-widest"
          >
            Check Device Age <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Problems;